/*
********************************************************************************
Functions related to PDF generation using jsPDF and jsPDF-AutoTable libraries
********************************************************************************
*/

//@ts-nocheck
// Toggle preceding from @ts-nocheck to @ts-check to enable TypeScript checking

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Browser } from "@capacitor/browser";

import { consoleMsg, getStoredValue, isLiveData } from "./utility.js";
import { combinedVehicleDetails, updateCombinedVehicleDetails } from "./app-data.js";
//prettier-ignore
import {
   addDataTable,
   addDetailsTable,
   addNotesTable,
   addCarImagesTable,
   resizeImageForPdf,
   calculateOffset,
   addFooter,
   savePDF
} from "./pdf-functions.js";

// Make sure our app references are using the global app object associated with window
const app = window.app;

export async function generateInspectionPDF() {
	const storageKey = app.state.storageKey;

	// Retrieve vehicle data from Ionic storage
	await updateCombinedVehicleDetails();

	// Define variables to store inspection photos
	// prettier-ignore
	const cars = [
    await store.get(`${storageKey}_car-front`), 
    await store.get(`${storageKey}_car-driver-side`), 
    await store.get(`${storageKey}_car-passenger-side`),
    await store.get(`${storageKey}_car-back`),
    await store.get(`${storageKey}_car-interior-front`),
    await store.get(`${storageKey}_car-interior-rear`),
    await store.get(`${storageKey}_car-vin`),
    await store.get(`${storageKey}_car-top`),
    await store.get(`${storageKey}_car-driver-profile`),
    await store.get(`${storageKey}_car-passenger-profile`),
    await store.get(`${storageKey}_car-driver-rear-profile`),
    await store.get(`${storageKey}_car-passenger-rear-profile`),
    await store.get(`${storageKey}_car-trunk`),
    await store.get(`${storageKey}_car-odometer`)
  ];

	const vehicleInfo = combinedVehicleDetails.vehicleInfo;
	const contactInfo = combinedVehicleDetails.contactInfo;
	const loanInfo = combinedVehicleDetails.loanInfo;
	const deliveryNotes = combinedVehicleDetails.deliveryNotes;

	const vendor = app.settings.vendor;
	const brand = combinedVehicleDetails.vehicleInfo["Brand"];
	const model = combinedVehicleDetails.vehicleInfo["Model"];
	const inspector = combinedVehicleDetails.loanInfo["Assigned Inspector"];

	// Define variables to store page data for insertion into AutoTable
	// NOTE: No form data on page 9, and separate functions to retrieve inspection photos
	const page1 = await store.get(`${storageKey}_page1`);
	const page2 = await store.get(`${storageKey}_page2`);
	const page3 = await store.get(`${storageKey}_page3`);
	const page4 = await store.get(`${storageKey}_page4`);
	const page5 = await store.get(`${storageKey}_page5`);
	const page6 = await store.get(`${storageKey}_page6`);
	const page7 = await store.get(`${storageKey}_page7`);
	const page8 = await store.get(`${storageKey}_page8`);
	const page10 = await store.get(`${storageKey}_page10`);

	// Define additional constants for Mercedes pages (only used for Mercedes workflows)
	const page11 = await store.get(`${storageKey}_page11`);
	const page12 = await store.get(`${storageKey}_page12`);
	const page13 = await store.get(`${storageKey}_page13`);
	const page14 = await store.get(`${storageKey}_page14`);

	// Constants for Mercedes outgoing pages
	const page15 = await store.get(`${storageKey}_page15`);
	const page16 = await store.get(`${storageKey}_page16`);
	const page17 = await store.get(`${storageKey}_page17`);

	// Constant for inspection direction (return vs. outgoing)
	const direction = app.data.navsData.direction;
	// Title-case equivalent of direction for injecting into PDF
	const directionTitle = direction[0].toUpperCase() + direction.slice(1);

	let releaseStatus = "Release"; // default

	if (!page10) {
		console.warn("Page 10 data missing or null");
	} else if (!page10.page10) {
		console.warn("Page 10 object present but missing expected .page10 array");
	} else {
		releaseStatus = page10.page10[1]?.value || "Release";
	}

	// Set line height multiplier
	const lineHeight = 1.35;

	const doc = new jsPDF({
		unit: "mm",
		lineHeight: lineHeight,
	});

	const margin = 24;
	const fontSize = 10;
	const pageWidth = Math.round(doc.internal.pageSize.width);
	const pageHeight = Math.round(doc.internal.pageSize.height);
	const maxLineWidth = pageWidth - 2 * margin;
	const charSpacing = 0; // Default = 0
	const sectionBreak = 5; // Variable to add spacing between sections

	// --- Named constants (change here to update everywhere) ---
	// Font sizes
	const titleFontSize = 18;
	const subtitleFontSize = 12;
	const sectionFontSize = 10;

	// Header bar dimensions
	const headerBarHeight = 8;
	const headerTextOffset = 5;
	const headerBottomSpacing = 3;

	// Colors
	//const colorPrimaryBlue = [74, 131, 192];
	const colorPrimaryBlue = [19, 108, 145];
	//const colorPrimaryBlue = [88, 128, 186];
	const colorGray = 169;
	const colorTitleGray = 128;

	// Logo dimensions
	const logoWidth = 45;
	const logoHeight = 11.4;
	const logoYOffset = 22.4;

	// Title spacing multiplier
	const titleSpacingMultiplier = 0.6;

	// Canvas base dimensions (pre-scale)
	const legendBaseWidth = 180;
	const legendBaseHeight = 18;
	const damageCanvasBaseHeight = 75.6;

	// Image resize widths in pixels
	const thumbnailResizeWidth = 400;
	const damagePhotoResizeWidth = 900;

	// Signature section
	const signatureMinSpace = 50;
	const signatureBoxHeight = 40;
	const signatureImageWidth = 100;
	const signatureImageHeight = 30;

	// Estimated car image row height for page-break check
	const estimatedCarRowHeight = 80;
	let pages = ["page2", "page3", "page4", "page5", "page6", "page7", "page8", "page11"];

	// Check if vendor is Mercedes and update pages accordingly
	// NOTE: page order as specified is important to properly create required PDF data tables
	if (vendor === "Mercedes-Benz") {
		if (direction === "return") {
			pages = ["page2", "page11", "page12", "page13", "page14", "page8"];
		} else {
			pages = ["page2", "page11", "page15", "page16", "page17"];
		}
	}

	// Set initial Y position and offset
	let yPosition = 10;
	let yOffset = 0;

	// Define variable to assign fill color
	let fillColor = 169;

	// Set metadata
	doc.setProperties({
		title: `${directionTitle} Inspection Summary`,
		subject: "Summary of vehicle inspection",
		author: "Page One Automotive",
		keywords: `inspection, vehicle, summary, ${brand}, ${model}, ${direction}`,
		creator: "jsPDF",
		custom: {
			driver: inspector,
		},
	});

	doc.setCharSpace(charSpacing);

	// Fetch images from Ionic storage
	const legendImage = await store.get(`${storageKey}_legend-image`);
	const damageImage = await store.get(`${storageKey}_damage-image`);
	const interiorDamageImage = await store.get(`${storageKey}_interior-damage-image`);
	const signatureImage = await store.get(`${storageKey}_signature`);
	const logoImage = await store.get(`${storageKey}_logo-image`);

	// X = width; Y = height.  To move down the page, increase Y.  To move right, increase X.
	doc.addImage(logoImage, "PNG", margin, yPosition, logoWidth, logoHeight);

	/* Not sure why, but need to add an additional 4 pixels to logo height to get text to offset
	properly: investigate */
	//yPosition += 29.8;
	yPosition += logoYOffset;

	// Add title
	let title = `${directionTitle} Inspection Summary`;

	doc.setFontSize(titleFontSize);
	doc.setFont("helvetica", "bold");
	doc.setTextColor(colorTitleGray);
	let xPosition = pageWidth / 2; // Calculate the horizontal position to center the text
	doc.text(title, xPosition, yPosition, null, null, "center");

	// Calculate y offset: font size * line height / pixes per mm
	yOffset = calculateOffset(doc, lineHeight);

	// Add the offset to the current Y position to move down the page
	yPosition += yOffset * titleSpacingMultiplier;

	// Add summary
	doc.setFontSize(subtitleFontSize);
	if (releaseStatus === "Hold") {
		title = "Vehicle Failed Inspection: Hold Issued";
	} else {
		title = "Vehicle Passed Inspection: Release Issued";
	}

	doc.text(title, xPosition, yPosition, null, null, "center");

	// Reset text color to black
	doc.setTextColor("#000000");

	// Recalculate offset and add to current yPosition
	yOffset = calculateOffset(doc, lineHeight);
	yPosition += yOffset * titleSpacingMultiplier;

	doc.setFont("Helvetica", "normal");

	title = "Inspection Details";
	doc.setFontSize(sectionFontSize);
	doc.setFillColor(...colorPrimaryBlue);
	doc.setTextColor(255); // IMPORTANT: Make sure to reset text color to black after setting to white here
	doc.rect(0 + margin, yPosition, pageWidth - margin * 2, headerBarHeight, "F");

	// Set y position of inserted text.  Previously had set to 4, but text was too high.  Apparently JSPdf inserts text from baseline
	yPosition += headerTextOffset;

	doc.text(title, xPosition, yPosition, null, null, "center");

	yPosition += headerBottomSpacing;
	let finalY = yPosition;

	function addTableHeader(title, finalY, ...fillColor) {
		yPosition = finalY;
		doc.setFontSize(sectionFontSize);
		doc.setFillColor(...fillColor);
		// Gray backgrounds (single-value grayscale) use dark text for contrast; colored backgrounds use white
		const textColor = fillColor.length === 1 ? 50 : 255;
		doc.setTextColor(textColor);
		doc.rect(0 + margin, yPosition, pageWidth - margin * 2, headerBarHeight, "F");

		yPosition += headerTextOffset;

		doc.text(title, xPosition, yPosition, null, null, "center");

		yPosition += headerBottomSpacing;

		// Reset text color to black
		doc.setTextColor(0);
	}

	const contCb =
		(title, ...colorArgs) =>
		(data) => {
			if (data.pageNumber > 1) {
				addTableHeader(`${title} (Continued)`, margin, ...colorArgs);
			}
		};

	function checkPageBreak(minHeight = 20) {
		if (finalY + minHeight > pageHeight - margin) {
			doc.addPage();
			finalY = margin;
			yPosition = margin;
		}
	}

	/*
	Overlay the inspector's entries onto the NAVS snapshot. dataTables is read-only, so
	anything edited on page 1 or the signature page exists only in stored page data.
	Values are looked up by id, never by position — see getStoredValue.
	*/
	const userStartingMileage = getStoredValue(page1, "page1", "starting-mileage");
	const userPlate = getStoredValue(page1, "page1", "license-plate-number");
	const userEndingMileage = getStoredValue(page10, "page10", "ending-mileage");

	if (userStartingMileage !== "") vehicleInfo["Starting Mileage"] = userStartingMileage;
	if (userPlate !== "") vehicleInfo["License Plate Number"] = userPlate;
	vehicleInfo["Ending Mileage"] = userEndingMileage !== "" ? userEndingMileage : (app.data.endingMileage ?? "");

	checkPageBreak(20);
	addTableHeader("Vehicle Information", finalY, fillColor);
	addDetailsTable(doc, vehicleInfo, margin, yPosition, contCb("Vehicle Information", colorGray));

	// Get the final Y position after the table is drawn
	finalY = doc.lastAutoTable.finalY;

	// Add direction to loan info (capitalize first letter)
	loanInfo["Direction"] = directionTitle;

	checkPageBreak(20);
	addTableHeader("Loan Information", finalY, fillColor);
	addDetailsTable(doc, loanInfo, margin, yPosition, contCb("Loan Information", colorGray));

	// Get the final Y position after the table is drawn
	finalY = doc.lastAutoTable.finalY;

	checkPageBreak(20);
	addTableHeader("Contact Information", finalY, fillColor);
	addDetailsTable(doc, contactInfo, margin, yPosition, contCb("Contact Information", colorGray));

	// Get the final Y position after the table is drawn
	finalY = doc.lastAutoTable.finalY;

	checkPageBreak(20);
	addTableHeader("Delivery Notes", finalY, fillColor);
	addNotesTable(doc, deliveryNotes, margin, yPosition, contCb("Delivery Notes", colorGray));

	finalY = doc.lastAutoTable.finalY;

	// Update fill color to blue for inspection section sub headers
	fillColor = colorPrimaryBlue;

	let currentPage;

	// Pre-process car images to thumbnail size before embedding in PDF
	const resizedCars = await Promise.all(
		cars.map(async (car) => {
			if (!car || !car.photo) return car;
			const resized = await resizeImageForPdf(car.photo, thumbnailResizeWidth);
			return { ...car, photo: resized };
		}),
	);

	const hasPhotos = resizedCars.some((car) => car?.photo);

	// Inspection Photos (car walkaround) — placement mirrors in-app page order: Photos falls right
	// after Tires for the "return" direction, but moves to last (just before Signature) for "delivery"
	function addInspectionPhotosSection() {
		if (!hasPhotos) return;

		// Estimate if table fits on current page
		const estimatedTableHeight = Math.ceil(cars.length / 4) * estimatedCarRowHeight; // rough estimate
		const headerHeight = 20; // Approximate height of addTableHeader
		if (finalY + headerHeight + estimatedTableHeight > pageHeight - margin) {
			doc.addPage();
			finalY = margin;
		}

		addTableHeader("Inspection Photos", finalY, ...colorPrimaryBlue);
		finalY += headerBarHeight; // Update finalY to account for header height
		finalY = addCarImagesTable(doc, resizedCars, margin, finalY, pageWidth);
		finalY = doc.lastAutoTable.finalY + sectionBreak;
	}

	// Glovebox Documents — right after Details, matching in-app page order
	// (page3 follows Details in every vendor/direction flow)
	checkPageBreak(20);
	addTableHeader("Glovebox Documents", finalY, ...fillColor);
	currentPage = "page3";
	addDataTable(doc, page3, margin, yPosition, currentPage, contCb("Glovebox Documents", ...colorPrimaryBlue));
	finalY = doc.lastAutoTable.finalY + sectionBreak;

	// Tire Inspection — always right after Glovebox Documents, matching in-app page order
	// (Tires follows Glovebox in every vendor/direction flow)
	checkPageBreak(20);
	addTableHeader("Tire Inspection", finalY, ...fillColor);
	currentPage = "page8";
	addDataTable(doc, page8, margin, yPosition, currentPage, contCb("Tire Inspection", ...colorPrimaryBlue));
	finalY = doc.lastAutoTable.finalY + sectionBreak;

	if (direction === "return") {
		addInspectionPhotosSection();
	}

	// Check if there's enough space for Exterior header + legend + damage canvas
	let scaleFactor = (pageWidth - 2 * margin) / legendBaseWidth;
	const exteriorNeededHeight = headerBarHeight + headerBottomSpacing + (legendImage ? legendBaseHeight * scaleFactor : 0) + (damageImage ? damageCanvasBaseHeight * scaleFactor : 0);
	checkPageBreak(exteriorNeededHeight);
	addTableHeader("Exterior Inspection", finalY, ...fillColor);

	// Add legend & damage image
	let imgWidth = legendBaseWidth;
	let imgHeight = legendBaseHeight;

	if (legendImage) {
		imgWidth = legendBaseWidth * scaleFactor;
		imgHeight = legendBaseHeight * scaleFactor;
		doc.addImage(legendImage, "PNG", margin, yPosition, imgWidth, imgHeight);
		yPosition += imgHeight;
	}

	if (damageImage) {
		imgHeight = damageCanvasBaseHeight * scaleFactor;
		doc.addImage(damageImage, "PNG", margin, yPosition, imgWidth, imgHeight);
		yPosition += imgHeight;
	}

	currentPage = "page2";

	addDataTable(doc, page2, margin, yPosition, currentPage, contCb("Exterior Inspection", ...colorPrimaryBlue));

	finalY = doc.lastAutoTable.finalY + sectionBreak;
	// Fetch all damage photos
	const keys = await store.keys();
	const damagePhotos = {};
	const interiorDamagePhotos = {};

	// Iterate through keys: store exterior damage separately from interior damage
	for (const key of keys) {
		if (key.includes("photoRow")) {
			const photoObject = await store.get(key);
			const photo = photoObject.photo;
			const ratio = photoObject.ratio;
			damagePhotos[key] = { photo, ratio };
		} else if (key.includes("interiorPhotoRow")) {
			const photoObject = await store.get(key);
			const photo = photoObject.photo;
			const ratio = photoObject.ratio;
			interiorDamagePhotos[key] = { photo, ratio };
		}
	}

	async function addDamagePhotos(collection, title) {
		if (Object.keys(collection).length === 0) return;

		const startPage = (continued) => {
			doc.addPage();
			finalY = margin;
			addTableHeader(continued ? `${title} (Continued)` : title, finalY, ...colorPrimaryBlue);
		};

		// Use current page if there's enough room, otherwise start a new one
		const minStartHeight = headerBarHeight + headerBottomSpacing + 30;
		if (finalY + minStartHeight > pageHeight - margin) {
			doc.addPage();
			finalY = margin;
		}
		addTableHeader(title, finalY, ...colorPrimaryBlue);

		for (const key in collection) {
			if (collection.hasOwnProperty(key)) {
				const { photo, ratio } = collection[key];
				const resizedPhoto = await resizeImageForPdf(photo, damagePhotoResizeWidth);
				const width = legendBaseWidth * scaleFactor;
				const height = width * ratio;
				if (yPosition + height > pageHeight - margin) {
					startPage(true);
				}
				doc.addImage(resizedPhoto, "JPEG", margin, yPosition, width, height);
				yPosition += height + sectionBreak;
			}
		}

		// Sync finalY so callers use the correct position after photos are placed
		finalY = yPosition;
	}

	// Add damage photos
	await addDamagePhotos(damagePhotos, "Exterior Damage Photos");

	if (vendor !== "Mercedes-Benz") {
		// Combined check: space for header + legend + interior damage canvas
		const interiorNeededHeight = headerBarHeight + headerBottomSpacing + (legendImage ? legendBaseHeight * scaleFactor : 0) + (interiorDamageImage ? damageCanvasBaseHeight * scaleFactor : 0);
		checkPageBreak(interiorNeededHeight);
		addTableHeader("Interior Inspection", finalY, ...fillColor);

		if (legendImage) {
			imgWidth = legendBaseWidth * scaleFactor;
			imgHeight = legendBaseHeight * scaleFactor;
			doc.addImage(legendImage, "PNG", margin, yPosition, imgWidth, imgHeight);
			yPosition += imgHeight;
		}

		if (interiorDamageImage) {
			imgHeight = damageCanvasBaseHeight * scaleFactor;
			doc.addImage(interiorDamageImage, "PNG", margin, yPosition, imgWidth, imgHeight);
			yPosition += imgHeight;
		}

		currentPage = "page11";
		addDataTable(doc, page11, margin, yPosition, currentPage, contCb("Interior Inspection", ...colorPrimaryBlue));

		finalY = doc.lastAutoTable.finalY + sectionBreak;

		await addDamagePhotos(interiorDamagePhotos, "Interior Damage Photos");

		checkPageBreak(20);
		addTableHeader("Interior Check", finalY, ...fillColor);

		currentPage = "page4";
		addDataTable(doc, page4, margin, yPosition, currentPage, contCb("Interior Check", ...colorPrimaryBlue));

		finalY = doc.lastAutoTable.finalY + sectionBreak;

		checkPageBreak(20);
		addTableHeader("Road Test", finalY, ...fillColor);

		currentPage = "page5";
		addDataTable(doc, page5, margin, yPosition, currentPage, contCb("Road Test", ...colorPrimaryBlue));

		finalY = doc.lastAutoTable.finalY + sectionBreak;

		checkPageBreak(20);
		addTableHeader("Lights Check", finalY, ...fillColor);

		currentPage = "page6";
		addDataTable(doc, page6, margin, yPosition, currentPage, contCb("Lights Check", ...colorPrimaryBlue));

		finalY = doc.lastAutoTable.finalY + sectionBreak;

		checkPageBreak(20);
		addTableHeader("Fluid Inspection", finalY, ...fillColor);

		currentPage = "page7";
		addDataTable(doc, page7, margin, yPosition, currentPage, contCb("Fluid Inspection", ...colorPrimaryBlue));

		finalY = doc.lastAutoTable.finalY + sectionBreak;
	} else {
		// Combined check: space for header + legend + interior damage canvas
		const interiorNeededHeight = headerBarHeight + headerBottomSpacing + (legendImage ? legendBaseHeight * scaleFactor : 0) + (interiorDamageImage ? damageCanvasBaseHeight * scaleFactor : 0);
		checkPageBreak(interiorNeededHeight);
		addTableHeader("Interior Inspection", finalY, ...fillColor);

		if (legendImage) {
			imgWidth = legendBaseWidth * scaleFactor;
			imgHeight = legendBaseHeight * scaleFactor;
			doc.addImage(legendImage, "PNG", margin, yPosition, imgWidth, imgHeight);
			yPosition += imgHeight;
		}

		if (interiorDamageImage) {
			imgHeight = damageCanvasBaseHeight * scaleFactor;
			doc.addImage(interiorDamageImage, "PNG", margin, yPosition, imgWidth, imgHeight);
			yPosition += imgHeight;
		}

		currentPage = "page11";
		addDataTable(doc, page11, margin, yPosition, currentPage, contCb("Interior Inspection", ...colorPrimaryBlue));

		finalY = doc.lastAutoTable.finalY + sectionBreak;

		await addDamagePhotos(interiorDamagePhotos, "Interior Damage Photos");

		if (direction === "return") {
			checkPageBreak(20);
			addTableHeader("Road Test", finalY, ...fillColor);

			currentPage = "page12";
			addDataTable(doc, page12, margin, yPosition, currentPage, contCb("Road Test", ...colorPrimaryBlue));

			finalY = doc.lastAutoTable.finalY + sectionBreak;

			checkPageBreak(20);
			addTableHeader("Function Check", finalY, ...fillColor);

			currentPage = "page14";
			addDataTable(doc, page14, margin, yPosition, currentPage, contCb("Function Check", ...colorPrimaryBlue));

			finalY = doc.lastAutoTable.finalY + sectionBreak;

			checkPageBreak(20);
			addTableHeader("Communication Check", finalY, ...fillColor);

			currentPage = "page13";
			addDataTable(doc, page13, margin, yPosition, currentPage, contCb("Communication Check", ...colorPrimaryBlue));

			finalY = doc.lastAutoTable.finalY + sectionBreak;
		} else {
			checkPageBreak(20);
			addTableHeader("Vehicle Settings", finalY, ...fillColor);

			currentPage = "page15";
			addDataTable(doc, page15, margin, yPosition, currentPage, contCb("Vehicle Settings", ...colorPrimaryBlue));

			finalY = doc.lastAutoTable.finalY + sectionBreak;

			checkPageBreak(20);
			addTableHeader("Central Display Settings", finalY, ...fillColor);

			currentPage = "page16";
			addDataTable(doc, page16, margin, yPosition, currentPage, contCb("Central Display Settings", ...colorPrimaryBlue));

			finalY = doc.lastAutoTable.finalY + sectionBreak;

			checkPageBreak(20);
			addTableHeader("Upon Delivery", finalY, ...fillColor);

			currentPage = "page17";
			addDataTable(doc, page17, margin, yPosition, currentPage, contCb("Upon Delivery", ...colorPrimaryBlue));

			finalY = doc.lastAutoTable.finalY + sectionBreak;
		}
	}

	// Inspection Photos (car walkaround) — placed last for the "delivery" direction, matching in-app order
	if (direction !== "return") {
		addInspectionPhotosSection();
	}

	// Update yPosition to finalY to ensure proper spacing after last table
	yPosition = finalY;

	// Check if there's enough space for signature section, add page break if needed
	if (yPosition + signatureMinSpace > pageHeight) {
		doc.addPage();
		yPosition = margin;
	}

	title = "Digital Signature";
	xPosition = pageWidth / 2;
	doc.setFontSize(sectionFontSize);
	doc.setFillColor(...colorPrimaryBlue);
	doc.setTextColor(255); // IMPORTANT: Make sure to reset text color to black after setting to white here
	doc.rect(0 + margin, yPosition, pageWidth - margin * 2, headerBarHeight, "F");

	yPosition += headerTextOffset;

	doc.text(title, xPosition, yPosition, null, null, "center");

	yPosition += headerBottomSpacing;

	doc.setDrawColor(227);

	doc.rect(margin, yPosition, pageWidth - margin * 2, signatureBoxHeight);

	//yPosition += yOffset;
	if (signatureImage) {
		doc.addImage(signatureImage, "PNG", margin, yPosition, signatureImageWidth, signatureImageHeight);
		yPosition += signatureBoxHeight;
	} else {
		yPosition += signatureBoxHeight;
	}
	// Reset text color before drawing inspector name (signature header leaves it white)
	doc.setTextColor(0);
	doc.setFontSize(10);
	doc.text(inspector ? `Signed by inspector: ${inspector}` : "", margin, yPosition + 5);
	// Right-justified signing timestamp on the same row as the inspector name (matches footer format)
	const signedDate = new Date();
	const signedTimestamp = `Date: ${signedDate.toLocaleDateString()} ${signedDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
	doc.text(signedTimestamp, pageWidth - margin, yPosition + 5, null, null, "right");
	// Add footer to each page
	addFooter(doc, margin, pageWidth, pageHeight, directionTitle);

	await store.set(`${storageKey}_inspection-summary`, doc.output("datauristring"));

	const pdfBlob = doc.output("blob");
	const fileName = "inspection_summary.pdf";

	// Dev/web mode: trigger PDF download using a blob URL (memory-efficient — avoids
	// embedding the full base64 string inline, which crashes low-memory mobile browsers)
	if (Capacitor.getPlatform() === "web" && !isLiveData()) {
		const blobUrl = URL.createObjectURL(pdfBlob);
		const link = document.createElement("a");
		link.href = blobUrl;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
	}

	// Save to filesystem (requires base64) — wrapped in a Promise so generateInspectionPDF
	// does not return before the save completes
	await new Promise((resolve) => {
		const reader = new FileReader();
		reader.readAsDataURL(pdfBlob);
		reader.onloadend = async function () {
			if (typeof reader.result === "string" && reader.result.includes(",")) {
				const base64data = reader.result.split(",")[1];
				if (!base64data) {
					console.error("No base64 data extracted from PDF.");
					resolve();
					return;
				}
				try {
					await savePDF(fileName, base64data);
				} catch (err) {
					console.error("Failed to save PDF:", err);
				}
			} else {
				console.error("FileReader result is not a valid data URL:", reader.result);
			}
			resolve();
		};
		reader.onerror = () => {
			console.error("FileReader error:", reader.error);
			resolve();
		};
	});
}
